/* oxlint-disable max-lines -- legacy file, split it before you add code */
import { Dialog as DialogRoot, DialogContent, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import { toast } from '@askable/ui/core/sonner';
import _ from 'lodash';
import momentTimezone from 'moment-timezone';
import { useState } from 'react';
import { useMutation } from 'urql';

import { Dialog, NoShowDialog, ParticipantIssueDialog } from 'components/common';
import ReInviteParticipant from 'components/manageBooking/inviteApplicants/invitationDialog/reInviteParticipant';
import InvitationDialog from 'components/manageBooking/inviteApplicants/invitationDialog/view';
import RevokeInvitationDialog from 'components/manageBooking/inviteApplicants/revokeInvitationDialog/revokeInvitationDialog';
import { CancelParticipantSession } from 'data/mutations/bookingSubmission/cancelParticipantSession.mutation';
import { ReInviteParticipantSession } from 'data/mutations/bookingSubmission/reInviteParticipantSession.mutation';
import { bookingUtils } from 'lib/booking';
import { utils } from 'lib/utils';
import { getCombinedErrorMessage } from 'utils/error_utils';

import SessionToReschedulePopover from './sessionToReschedulePopover';

import './actionsContainer.scss';

function ActionsContainer(props: any) {
  const [openCancelParticipantDialog, setOpenCancelParticipantDialog] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openReinviteParticipant, setOpenReinviteParticipant] = useState(false);
  const [loadingReinvitation, setLoadingReinvitation] = useState(false);
  const [openRevokeInviteModal, setOpenRevokeInviteModal] = useState(false);
  const [openNoShowDialog, setOpenNoShowDialog] = useState(false);
  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openInviteParticipantDialog, setOpenInviteParticipantDialog] = useState(false);
  const [singleSessionWaitlisted, setSingleSessionWaitlisted] = useState<any>(undefined);

  const firstName = _.get(props.participantSelected, 'user.meta.identity.firstname');
  const startSession = momentTimezone(_.get(props.sessionSelected, 'start'))
    .tz(props.timezone)
    .format('ddd Do MMM hh:mm');
  const endSession = momentTimezone(_.get(props.sessionSelected, 'end')).tz(props.timezone).format('hh:mm a');
  const singleSession = {
    session: _.get(props, 'sessionSelected'),
    participant: {
      ..._.get(props, 'participantSelected'),
      invitedSession: {
        _id: _.get(props, 'participantSelected._id'),
      },
      user: _.get(props, 'participantSelected.user'),
    },
    booking_participant_id: _.get(props, 'participantSelected._id'),
    isAvailableForSession: true,
  };
  const hasRating =
    _.get(props.participantSelected, 'rating.engagement') || _.get(props.participantSelected, 'rating.overall');

  const [{ fetching: cancellingParticipant }, cancelParticipantSession] = useMutation(CancelParticipantSession);
  const [, reInviteParticipantSession] = useMutation(ReInviteParticipantSession);

  const onCancelParticipant = async () => {
    if (cancellingParticipant) return;
    const result = await cancelParticipantSession({
      booking_id: props.booking._id,
      participant_session_id: _.get(props, 'participantSelected._id'),
    });
    if (result.error) {
      toast.error(getCombinedErrorMessage(result.error, 'Error cancelling participant'));
      return;
    }
    props.onClose();
  };

  const onSendMessage = () => {
    props.onSendMessage(_.get(props, 'participantSelected'));
    props.onClose();
  };

  const onCloseSessionToReschedulePopover = () => {
    setOpenRescheduleModal(false);
    props.onClose();
  };

  const onConfirmReInvitation = async () => {
    setLoadingReinvitation(true);

    const { error } = await reInviteParticipantSession({
      booking_id: props.booking._id,
      participant_session_id: _.get(props, 'participantSelected._id'),
    });
    setLoadingReinvitation(false);
    if (error) {
      toast.error(getCombinedErrorMessage(error, 'Error reinviting participant'));
      return;
    }
    props.onClose();
  };

  const renderActionContainer = (iconPath: any, label: any, action: any, id?: any) => {
    return (
      <div className={`action ${action ? 'hasAction' : 'noAction'}`} onClick={action} id={id}>
        <div className="iconContainer">
          <img alt={`icon-${label}`} src={iconPath} className="actionIcon" />
        </div>
        <span>{label}</span>
      </div>
    );
  };

  const renderParticipantIssueContainer = () => {
    return renderActionContainer('/icons/issueIcon.svg', 'There was an issue', () => {
      setOpenIssueDialog(true);
    });
  };

  const renderContextActions = () => {
    // Confirmed participant (upcoming session) actions:
    //   Cancel Participant
    //   Reschedule
    // Confirmed participant (happening session) actions:
    //   Mark as no show
    // Invited participant actions:
    //   Resend invite
    //   Invite to different time
    //   Revoke invite
    // Completed participant actions:
    //   Mark as no show
    const isInPast = utils.isInPast(props.sessionSelected.end, _.get(props.booking, 'config.timezone'));
    const sessionStarted =
      utils.isInPast(props.sessionSelected.start, _.get(props.booking, 'config.timezone')) && !isInPast;

    // Waitlisted participant
    if (_.get(props, 'sessionStatus.label') === 'Waitlisted') {
      const sessionParticipants = _.get(props, 'sessionSelected.participants');
      const invitedParticipants = sessionParticipants.filter(
        (participant: any) => participant.status === 4 && participant.cancel === 0,
      );
      const confirmedParticipants = sessionParticipants.filter(
        (participant: any) => participant.status === 1 && participant.cancel === 0,
      );

      return (
        <div className="contextContainer">
          {confirmedParticipants.length === 0 &&
            renderActionContainer('/icons/sendInvite.svg', 'Invite', () => {
              let sessionStatus;

              // It should check how many participants got invited for session
              if (invitedParticipants.length > 0) {
                sessionStatus = {
                  label: `(${invitedParticipants.length} ${invitedParticipants.length === 1 ? 'person' : 'people'} invited)`,
                  qtd: invitedParticipants.length,
                  status: 'invited',
                };
              }

              setSingleSessionWaitlisted({
                session: _.get(props, 'sessionSelected'),
                sessionStatus,
                participant: {
                  ..._.get(props, 'participantSelected'),
                  user: _.get(props, 'participantSelected.user'),
                },
                participant_session_id: _.get(props, 'participantSelected._id'),
                isAvailableForSession: true,
              });
              setOpenInviteParticipantDialog(true);
            })}
          {renderActionContainer('/icons/cancelParticipant.svg', 'Cancel participant', () =>
            setOpenCancelParticipantDialog(true),
          )}
        </div>
      );
    }

    // Confirmed participant (upcoming session) actions:
    if (_.get(props, 'sessionStatus.label') === 'Confirmed' && !isInPast) {
      return (
        <div className="contextContainer">
          {renderActionContainer('/icons/cancelParticipant.svg', 'Cancel participant', () =>
            setOpenCancelParticipantDialog(true),
          )}
          {sessionStarted &&
            !hasRating &&
            renderActionContainer('/icons/noShow.svg', "Person didn't show up", () => setOpenNoShowDialog(true))}
          {sessionStarted && !hasRating && renderParticipantIssueContainer()}
          {!bookingUtils.isFocusGroup(props.booking) &&
            renderActionContainer(
              '/icons/rescheduleParticipant.svg',
              'Reschedule',
              () => {
                setOpenRescheduleModal(true);
              },
              'rescheduleAction',
            )}
        </div>
      );
    }

    // Confirmed participant (happening session) actions:
    if (_.get(props, 'sessionStatus.label') === 'Confirmed' && isInPast) {
      return (
        <div className="contextContainer">
          {renderActionContainer('/icons/noShow.svg', "Person hasn't shown up", () => setOpenNoShowDialog(true))}
        </div>
      );
    }

    // Invited participant actions:
    if (_.get(props, 'sessionStatus.class') === 'invited') {
      return (
        <div className="contextContainer">
          {renderActionContainer('/icons/resendInvite.svg', 'Resend invite', () => setOpenReinviteParticipant(true))}
          {renderActionContainer('/icons/revokeInvite.svg', 'Revoke invite', () => setOpenRevokeInviteModal(true))}
        </div>
      );
    }

    // Completed participant actions:
    if (_.get(props, 'sessionStatus.label') === 'Completed') {
      return (
        <div className="contextContainer">
          {!hasRating &&
            renderActionContainer('/icons/noShow.svg', "Person didn't show up", () => {
              setOpenNoShowDialog(true);
            })}
          {!hasRating && renderParticipantIssueContainer()}
        </div>
      );
    }
  };

  return (
    <div className="actionsComponent">
      {renderContextActions()}
      {renderActionContainer('/icons/sendMessage.svg', 'Send message', () => onSendMessage())}
      <hr />
      {renderActionContainer('/icons/viewOnParticipantsPage.svg', 'View on participant page', () => {
        props.viewParticipantsScreen();
      })}
      {openCancelParticipantDialog && (
        <Dialog
          title={`Are you sure you want to cancel ${firstName}'s session?`}
          modal={false}
          open={openCancelParticipantDialog}
          onRequestClose={() => setOpenCancelParticipantDialog(false)}
          customActions={[
            {
              label: 'No',
              primary: true,
              customFunction: () => setOpenCancelParticipantDialog(false),
              disabled: cancellingParticipant,
            },
            {
              label: 'Yes',
              primary: true,
              customFunction: () => onCancelParticipant(),
              disabled: cancellingParticipant,
            },
          ]}
        >
          <>
            <h3>Session details</h3>
            <p>
              {startSession} - {endSession}
            </p>
            <p>
              <strong>Note:</strong> {firstName} will get notified
            </p>
          </>
        </Dialog>
      )}
      {openRescheduleModal && (
        <DialogRoot open={openRescheduleModal} onOpenChange={setOpenRescheduleModal}>
          <DialogContent className="modalRescheduleParticipantInContext" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="sr-only">Reschedule session</DialogTitle>
            </DialogHeader>
            <div className="text-pretty text-sm text-muted-foreground">
              <SessionToReschedulePopover
                sessionToReschedule={props.sessionSelected}
                timezone={props.timezone}
                booking_id={_.get(props, 'booking._id')}
                sessionDuration={_.get(props, 'booking.config.session.duration')}
                onClose={onCloseSessionToReschedulePopover}
                sessionSelected={{
                  ...props.sessionSelected,
                  username: _.get(props, 'participantSelected.user.meta.identity.firstname'),
                }}
              />
            </div>
          </DialogContent>
        </DialogRoot>
      )}
      {openReinviteParticipant && (
        <ReInviteParticipant
          {...props}
          open={openReinviteParticipant}
          onCloseInvitationDialog={() => setOpenReinviteParticipant(false)}
          singleSession={singleSession}
          loading={loadingReinvitation}
          onConfirm={onConfirmReInvitation}
          showConfirmationMessage={false}
        />
      )}
      {openRevokeInviteModal && (
        <RevokeInvitationDialog
          {...props}
          open={openRevokeInviteModal}
          onClose={() => {
            setOpenRevokeInviteModal(false);
            props.onClose();
          }}
          singleSession={singleSession}
        />
      )}
      {openNoShowDialog && (
        <NoShowDialog
          open={openNoShowDialog}
          onClose={() => setOpenNoShowDialog(false)}
          onClickDone={props.onClose}
          participant={_.get(props, 'participantSelected')}
          booking={props.booking}
        />
      )}

      {openIssueDialog && (
        <ParticipantIssueDialog
          open={openIssueDialog}
          onClose={() => setOpenIssueDialog(false)}
          onClickDone={props.onClose}
          participant={_.get(props, 'participantSelected')}
          booking={props.booking}
        />
      )}
      {openInviteParticipantDialog && (
        <InvitationDialog
          {...props}
          open={openInviteParticipantDialog}
          onCloseInvitationDialog={() => setOpenInviteParticipantDialog(false)}
          singleSession={singleSessionWaitlisted!} // This is set before openInviteParticipantDialog
          isBulkInvite={false}
          onRefreshTable={() => null}
          showConfirmationMessage={false}
          onFinishInvitationProcess={() => props.onClose()}
        />
      )}
    </div>
  );
}

export default ActionsContainer;
